import React, { KeyboardEventHandler, MouseEventHandler, useContext } from 'react';
import Input from '../../../elements/Input';
import { isNilOrEmpty, removeInvalidCharacters, replaceAll } from '../../../../helpers/utils';
import { Context } from '@front10/helpers/dist/Context';
import { CSSProperties } from '@front10';
import Text from '../../../elements/Text';
import Box from '../../../elements/Box';
import Button from '../../../elements/Button';
import Icon from '../../../elements/Icon';
import {
  NavigationValidationFunctions,
  useCalendarDispatch,
  useCalendarState,
  useHandleCalendarNavigation
} from './hooks';
import { searchSeparator } from '../../../../helpers/date';
import VisuallyHidden from '@reach/visually-hidden';
import { DayData } from '../types';
import {
  addMonthsNumber,
  getDayData,
  getFirstDayOfMonth,
  getModifiers,
  isDayInRange,
  lastDayOfMonth,
  getAddDays
} from './helper';
import { getUuidMin } from '@front10/helpers/dist/uuid';

const DATE_OUT_OF_RANGE = 0;
const INCORRECT_FORMAT = 1;
const SPACE_KEY = 32;
const VALID_DATE = 2;
const separatorPositions = (source: string, value: string) => {
  let indices = [];
  for (let i = 0; i < source.length; i++) {
    if (source[i] === value) {
      indices.push(i);
    }
  }
  return indices;
};
const isOnArray = (array: Array<number>, element: number) => {
  for (let i = 0; i < array.length; i++) {
    if (array[i] === element) {
      return true;
    }
  }
  return false;
};

export type InputDatePickerProps = {
  allowedCharacters: string;
  component?: 'from' | 'to';
  className?: string;
  style?: CSSProperties;
  clearButton: any;
  inputRef: any;
  componentReference: any;
  clearAction: Function;
  format: string;
  inputItem: any;
  inputPlaceholderLabelKey: string;
  iconClose: string;
  invalidClass: string;
  labelKey: string;
  onValueClass: string;
  focusClass?: string;
  onFocus?: Function;
  onBlur?: Function;
  onDayClick?: (data: DayData, component: 'start' | 'end' | undefined) => void;
  onChange?: Function;
  onClick?: Function;
  onKeyDown?: Function;
  openPopupOnKeyDown?: boolean;
  openPopupOnClick?: boolean;
  textLabel: Record<string, object>;
  rightIcon: any;
  rightIconOpen: any;
  showCloseIcon: boolean;
  showLabel: boolean;
  valid: boolean;
  closePopoverAfterAction?: boolean;
  isRequired?: boolean;
  isClearEmptyInput?: boolean;
  value?: string | Date;
  errorLabelComponent?: any;
  firstAvailableDay?: number;
  useAsSecondary?: boolean;
  buttonRightIconId?: string;
  buttonClearId?: string;
  onBlurRightnButton?: () => void;
  handleKeyDown?: (e: any) => void;
  children?: any;
};
const InputDatePicker = (props: InputDatePickerProps) => {
  const {
    allowedCharacters,
    component = 'from',
    clearButton,
    clearAction,
    className = '',
    style = {},
    componentReference,
    format = 'DD MMM YYYY',
    inputRef = null,
    iconClose,
    closePopoverAfterAction = false,
    invalidClass,
    focusClass = '',
    inputPlaceholderLabelKey = 'cmp-label--datepicker-depart',
    inputItem,
    labelKey = 'cmp-date-range-selector-label',
    onValueClass,
    onChange = () => {},
    onClick = () => {},
    onFocus = () => {},
    onDayClick = () => {},
    openPopupOnKeyDown,
    openPopupOnClick,
    rightIcon: { icon: rightIcon, ...rightIconSettings },
    rightIconOpen,
    showLabel,
    textLabel,
    showCloseIcon,
    value,
    valid = true,
    errorLabelComponent,
    firstAvailableDay,
    onBlurRightnButton,
    isRequired = false,
    isClearEmptyInput = false,
    buttonRightIconId,
    buttonClearId,
    children
  } = props;

  const { state, modifiers, numberOfMonths } = useCalendarState();
  const dispatch = useCalendarDispatch();
  const context = useContext(Context);
  const { translate } = context;

  //validations functions
  const validations: NavigationValidationFunctions = {
    validateDay: (date: Date) => {
      const dateModifiers = getModifiers(date, modifiers);
      return (
        !dateModifiers.disabled &&
        dateModifiers.interactive &&
        !dateModifiers.blackout &&
        !dateModifiers.hidden
      );
    },
    dataFromDay: (date: Date) => {
      return getDayData(date, state.months);
    },
    isDateOutOfView: (date: Date) => {
      //show data when are off screen
      const endDisplayRange = lastDayOfMonth(
        addMonthsNumber(state.displayDate, numberOfMonths - 1)
      );
      const startDisplayRange = getFirstDayOfMonth(state.displayDate);
      return !isDayInRange(date, { start: startDisplayRange, end: endDisplayRange });
    }
  };

  const navigationHandler = useHandleCalendarNavigation({
    isPopopverOpen: state.popoverOpen,
    validations,
    initialDate: state.preSelected,
    dispatch,
    format,
    inputRef,
    component: 'start',
    onDayClick,
    preSelected: state.preSelected,
    availableDays: state.availableDays
  });

  const label = component === 'from' ? 'depart' : 'return';
  const componentId = getUuidMin();
  const classNameCmp = `fr-dates-range-selector ${
    !valid ? `${invalidClass || 'fr-input-invalid'}` : ''
  }`;

  let currentOnValueClass: string | undefined = '';
  if (!isNilOrEmpty(onValueClass) && !isNilOrEmpty(value)) {
    currentOnValueClass = onValueClass;
  }
  const separator = searchSeparator(format);

  const handleClick: MouseEventHandler = (event) => {
    onClick(event);
  };

  const handleOnChange = (event: any) => {
    if (isClearEmptyInput && event.target.value.length === 0) {
      dispatch({ type: 'CLEAR_ACTION' });
    }
    onChange(event);
  };
  const handleRightButtonAction: MouseEventHandler<HTMLButtonElement> = (event) => {
    // if (showCloseIcon) {
    //   clearAction(event);
    // }
    // dispatch({ type: 'CLEAR_ACTION' });
    // dispatch({
    //   type: 'SET_PRESELECTED_DATE',
    //   payload: state.today
    // });
    dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: state.today });
    dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
    if (!state.popoverOpen) {
      dispatch({ type: 'OPEN_POPOVER_ACTION' });
    } else {
      if (closePopoverAfterAction) {
        dispatch({ type: 'CLOSE_POPOVER_ACTION' });
      }
    }
    onClick(event);
  };
  const handleOnFocus = (event: $TSFixMe) => {
    if (!state.valid) {
      if (inputRef && inputRef.current) {
        inputRef?.current?.classList.add(''.concat(focusClass || 'fr-focus:focus'));
      }
    }
    onFocus(event);
  };

  const handleKeyDown: KeyboardEventHandler = (event: $TSFixMe) => {
    event.persist();

    const regex = new RegExp(allowedCharacters);
    if (!regex.test(event.key)) {
      event.preventDefault();
    }

    if (regex.test(event.key) && event.keyCode === SPACE_KEY) {
      event.target.value += ' ';
      event.preventDefault();
    }

    if (event.key === 'ArrowDown' && openPopupOnKeyDown) {
      dispatch({ type: 'SET_DAY_FOCUS_BY_KEYBOARD', payload: true });
      dispatch({ type: 'OPEN_POPOVER_ACTION' });
    }

    setTimeout(() => {
      if (event.key !== 'Backspace' && /^[0-9]$/i.test(event.key)) {
        if (event.target.value) {
          const separatorIndexs = separatorPositions(format, separator);
          if (separatorPositions.length !== 0) {
            const pos = event.target.selectionStart;
            if (isOnArray(separatorIndexs, pos)) {
              let newValue =
                event.target.value.substring(0, pos) +
                separator +
                event.target.value.substring(pos, event.target.value.length);
              const valueSeparatorIndexs = separatorPositions(newValue, separator);
              if (valueSeparatorIndexs.length > separatorIndexs.length) {
                const index = newValue.lastIndexOf(separator);
                newValue =
                  newValue.substring(0, index) + newValue.substring(index + 1, newValue.length);
              }
              event.target.value = newValue;
              inputRef.current.value = newValue;
              inputRef.current.selectionStart = pos + 1;
              inputRef.current.selectionEnd = pos + 1;
              dispatch({
                type: 'SET_INPUT_VALUE',
                payload: newValue,
                component: 'start'
              });
            }
          }
        }
      }
    }, 100);

    if (event.target.value === '' && event.key === 'Backspace') {
      dispatch({ type: 'CLEAR_ACTION' });
    }
    navigationHandler(event);
    if (props.handleKeyDown) {
      props.handleKeyDown(event);
    }
  };

  const inputContainerRef = React.useRef<any>(null);

  return (
    <Box display="block" shadow="none" {...inputItem?.container}>
      {showLabel && (
        <Text
          display="inline-block"
          textAlign="left"
          width="100"
          id={`input-label-date-range-${componentId}`}
          htmlFor={`input-${componentId}`}
          {...textLabel}
        >
          {translate(labelKey, 'Dates')}
        </Text>
      )}
      <Box
        border
        display="flex"
        className={`${className} ${classNameCmp}`}
        style={{ overflow: 'hidden', ...style }}
        dataTestId="dateRangeSelector"
        bgColor="white"
        focusReferences={(e) => {
          inputContainerRef.current = e;
        }}
      >
        <Box
          className={`fr-input fr-input-group fr-w-100 ${currentOnValueClass}`}
          focusReferences={componentReference}
        >
          <VisuallyHidden aria-live="polite">
            {!state.valid &&
              state.typeError === INCORRECT_FORMAT &&
              translate(
                `cmp-label-datepicker-depart-error-incorrect-format-date`,
                `Wrong format on the depart date.`
              )}
            {!state.valid &&
              state.typeError === DATE_OUT_OF_RANGE &&
              translate(
                `cmp-label-datepicker-depart-error-date-out-of-range`,
                `Departure date out of range.`
              )}
          </VisuallyHidden>
          <Input
            id={`${componentId}`}
            buttonRightIconId={buttonRightIconId}
            focusReferences={inputRef}
            value={state.inputValue.start}
            border={false}
            paddingRight="1"
            autoComplete={'off'}
            spellCheck={'off'}
            autoCorrect={'off'}
            ariaAutoComplete={'list'}
            className="fr-flex-2"
            defaultValue={value}
            placeholder={translate(inputPlaceholderLabelKey, 'Depart')}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onClick={openPopupOnClick ? handleClick : () => {}}
            onKeyDown={handleKeyDown}
            onRightIconClick={handleRightButtonAction}
            leftIcons={['calendar']}
            leftTabIndex={[-1]}
            size={10}
            onRightIconBlur={() => {
              if (inputContainerRef && inputContainerRef?.current) {
                inputContainerRef?.current?.classList.remove('fr-input-invalid');
              }
              if (state.typeError !== VALID_DATE) {
                dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
              }
            }}
            onBlur={(e) => {
              const relatedTargetId = (e?.relatedTarget as Element)?.getAttribute('id');
              if (relatedTargetId !== buttonRightIconId && relatedTargetId !== buttonClearId) {
                if (inputContainerRef && inputContainerRef?.current) {
                  inputContainerRef?.current?.classList.remove('fr-input-invalid');
                }
                if (state.typeError !== VALID_DATE) {
                  dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                }
              }
              if (inputRef && inputRef.current) {
                inputRef?.current?.classList.remove('fr-focus:focus');
              }
              if (props.onBlur) {
                props.onBlur(e);
              }
            }}
            rightAriaLabel={
              state.popoverOpen
                ? [translate('cmp-selector-aria-label-close-popover', 'Close popover')]
                : [translate('cmp-selector-aria-label-open-popover', 'Open popover')]
            }
            ariaDescribedby={`fr-${label}-label-${componentId}`}
            ariaLabelledBy={showLabel ? `input-label-date-range-${componentId}` : null}
            ariaLabelDescription={`${translate(
              'cmp-date-range-selector-instructions',
              'Type the date in this format'
            )} ${translate(
              `date-format-${removeInvalidCharacters(replaceAll(format, ' ', '-'))}`,
              format
            )}`}
            showFocusVisualCue={false}
            {...inputItem}
          />
          {showCloseIcon ? (
            <Box className="fr-input-group-append">
              <Button
                color="transparent"
                ariaLabel={translate(
                  `cmp-date-range-selector-${showCloseIcon ? 'clear-filter' : 'right-icon'}`,
                  'Clear'
                )}
                paddingWidth="2"
                dataTestId="ClearButton"
                {...clearButton}
                onClick={(event) => {
                  clearAction(event);
                  const newDate =
                    firstAvailableDay !== undefined && firstAvailableDay !== null
                      ? getAddDays(state.today, firstAvailableDay)
                      : state.today;

                  dispatch({
                    type: 'SET_PRESELECTED_DATE',
                    payload: newDate
                  });
                  dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: newDate });
                }}
                onKeyDown={() => {
                  // if (!this.isFocused && this.popover && !event.shiftKey && event.keyCode === 9)
                  //   this.popover.closePopover();
                }}
                tabIndex={showCloseIcon ? null : -1}
                onBlur={(e) => {
                  if (inputContainerRef && inputContainerRef?.current) {
                    inputContainerRef?.current?.classList.remove('fr-input-invalid');
                  }
                  const relatedTargetId = (e?.relatedTarget as Element)?.getAttribute('id');
                  if (relatedTargetId !== buttonRightIconId && relatedTargetId !== buttonClearId) {
                    if (inputContainerRef && inputContainerRef?.current) {
                      inputContainerRef?.current?.classList.remove('fr-input-invalid');
                    }
                    if (state.typeError !== VALID_DATE) {
                      dispatch({ type: 'RESET_ACTION_AND_CLEAR_STATE' });
                    }
                  }
                  onBlurRightnButton?.();
                }}
                id={buttonClearId}
              >
                <Icon
                  align="middle"
                  icon={
                    showCloseIcon ? iconClose : state.popoverOpen ? rightIconOpen.icon : rightIcon
                  }
                  {...rightIconSettings}
                />
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box color={'red'} {...errorLabelComponent}>
        {!state.valid &&
          state.typeError === INCORRECT_FORMAT &&
          translate(
            `cmp-label-datepicker-depart-error-incorrect-format-date`,
            `Wrong format on the depart date.`
          )}
        {(state.inputValue.start === '' || state.inputValue.start === undefined) &&
          isRequired &&
          translate(`cmp-label-datepicker-depart-error-empty-input`, `Input departure date`)}
        {!state.valid &&
          state.typeError === DATE_OUT_OF_RANGE &&
          translate(
            `cmp-label-datepicker-depart-error-date-out-of-range`,
            `Departure date out of range.`
          )}
      </Box>
      {children}
    </Box>
  );
};

export default InputDatePicker;
